import React, { useState } from 'react';
import { SpaceBetween, Link, Icon } from '@amzn/awsui-components-react';

/**
 * Props interface for the ExpandableList component
 * @interface ExpandableListProps
 */
export interface ExpandableListProps<T> {
  /** Array of data items to be rendered in the list */
  data: T[];
  /** Maximum number of items to show when list is collapsed */
  maxVisible: number;
  /** Function to render each item in the list */
  renderItem: (element: T) => React.ReactNode;
  /** Optional content to display when list is empty */
  emptyState?: React.ReactNode;
  /** Optional text for the "Show More" button. */
  showMoreText?: string;
  /** Optional text for the "Show Less" button. */
  showLessText?: string;
}

/**
 * A React component that renders an expandable list of items with show more/less functionality
 * 
 * @param {ExpandableListProps} props - The component props
 * 
 * @returns A React element containing the expandable list
 */
export const ExpandableList = <T,>(props: ExpandableListProps<T>) => {
  const [expanded, setExpanded] = useState(false);

  if (!props.data || props.data.length === 0) {
    return props.emptyState;
  }

  const visibleItems = expanded ? props.data : props.data.slice(0, props.maxVisible);

  return (
    <div>
      <SpaceBetween direction="vertical" size="xs">
        {visibleItems.map((element, index) => (
          <div key={index}>
            {props.renderItem(element)}
          </div>
        ))}
      </SpaceBetween>

      {props.data.length > props.maxVisible && (
        <Link
          variant="primary"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-controls="items-list"
        >
          <SpaceBetween direction="horizontal" size="xxs">
            <span key="text">{expanded ? props.showLessText : props.showMoreText}</span>
            <Icon key="icon" name={expanded ? "angle-up" : "angle-down"} />
          </SpaceBetween>
        </Link>
      )}
    </div>
  );
}
